import { Box, Icon, Stack } from "@mui/material";
import { Custom, Field, Popup } from "components";
import { useApp, useService, useTheme } from "contexts";
import { Validations } from "helpers";
import { LinkRounded, CloseRounded } from "@mui/icons-material";
import { Enums, Helpers } from "utils";
import { MessageType, OptionType, StringOrOptionType } from "types";
import { IUser } from "interfaces";
import { useShare } from "hooks";
import { useState } from "react";

type Props = {
  id: string;
  owner: string;
  open: boolean;
  onClose: () => void;
  participants: IUser[];
};

export const Invitation = ({
  id,
  open,
  onClose,
  owner,
  participants,
}: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();

  const [user, setUser] = useState<string>("");
  const [confirmationOpened, setConfirmationOpened] = useState<boolean>(false);

  const {
    currentParticipants,
    handleOnChange,
    handleOnCopy,
    handleOnSubmit,
    removeParticipants,
    users,
  } = useShare(id, participants, Enums.EnumShare.Invite, onClose);

  const handleOnRemove = async (user: string) => {
    setUser(user);
    setConfirmationOpened(true);
  };

  const handleOnConfirm = async () => {
    await removeParticipants([user]);
    setConfirmationOpened(false);
  };

  return (
    <>
      <Custom.Modal
        open={open}
        onClose={onClose}
        title={t.component.popup.invitation.title}
      >
        <Box>
          <Stack spacing={theme.spacing.md}>
            <Custom.Typography
              align="center"
              size={theme.font.xs}
              weight={theme.font.bold}
            >
              {t.component.popup.invitation.headline}
            </Custom.Typography>
          </Stack>
        </Box>

        <Box mt={theme.spacing.md}>
          <Stack spacing={theme.spacing.sm}>
            <Field.DynamicAutocomplete
              options={users}
              onValidate={Validations.isEmail}
              onChange={handleOnChange}
            />
            <Custom.Button variant="contained" onClick={handleOnSubmit}>
              {t.action.invite}
            </Custom.Button>
          </Stack>
        </Box>

        <Box mt={theme.spacing.md}>
          <Stack spacing={theme.spacing.sm}>
            <Custom.Button
              fullWidth
              variant="outlined"
              endIcon={<LinkRounded />}
              color={theme.color.accent.color}
              onClick={handleOnCopy}
            >
              {t.action.copy_link}
            </Custom.Button>
          </Stack>
        </Box>

        <Box mt={theme.spacing.lg}>
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={theme.spacing.md}
          >
            <Custom.Typography size={theme.font.xs}>
              {t.component.popup.invitation.header}
            </Custom.Typography>

            <Box
              sx={{
                width: 1,
                border: 1,
                maxHeight: 250,
                overflowY: "auto",
                borderRadius: theme.border.radius,
                borderColor: theme.color.border.accent,
              }}
            >
              {/* List of invited people */}
              {currentParticipants.map((participant: IUser, index: number) => {
                const isLast = currentParticipants.length === index - 1;
                return (
                  <Stack
                    key={index}
                    direction="row"
                    sx={{
                      p: theme.spacing.sm,
                      alignItems: "center",
                      borderBottom: isLast ? 0 : 1,
                      justifyContent: "space-between",
                      mb: isLast ? theme.spacing.sm : 0,
                      borderColor: theme.color.border.accent,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={theme.spacing.xs}
                    >
                      <Custom.Avatar
                        alt={participant.first_name}
                        src={Helpers.getUserAvatar(participant._id)}
                      />
                      <Custom.Typography size={theme.font.xs}>
                        {participant.full_name}
                      </Custom.Typography>
                    </Stack>
                    {owner !== participant._id && (
                      <Custom.Button
                        size="small"
                        endIcon={<CloseRounded />}
                        onClick={() => handleOnRemove(participant._id)}
                        sx={{ color: theme.color.status.error.color }}
                      >
                        {t.action.remove}
                      </Custom.Button>
                    )}
                  </Stack>
                );
              })}
            </Box>
          </Stack>
        </Box>
      </Custom.Modal>
      <Popup.Confirmation
        open={confirmationOpened}
        onClose={() => setConfirmationOpened(false)}
        onConfirm={handleOnConfirm}
      />
    </>
  );
};
